/**
 * Preferințe tabel conosamente (filtre, sortare, coloane, paginare),
 * stocate în localStorage pe direcție (import / export separat).
 */

export type BillsSortKey =
  | 'no'
  | 'company'
  | 'shipper'
  | 'consignee'
  | 'route'
  | 'vessel'
  | 'motherVessel'
  | 'ts1'
  | 'ts2'
  | 'containers'
  | 'issued'
  | 'onBoard'
  | 'eta'
  | 'type'
  | 'oceanFreight'
  | 'status'
  | 'note';

export type BillsSortDir = 'asc' | 'desc';

/** Coloane configurabile (ordinea default). Coloana actions e mereu la final. */
export type BillColumnId =
  | 'no'
  | 'company'
  | 'shipper'
  | 'consignee'
  | 'route'
  | 'vessel'
  | 'motherVessel'
  | 'ts1'
  | 'ts2'
  | 'containers'
  | 'issued'
  | 'onBoard'
  | 'eta'
  | 'type'
  | 'oceanFreight'
  | 'status'
  | 'note';

export interface BillColumnState {
  id: BillColumnId;
  visible: boolean;
}

export interface BillsTablePrefs {
  statusFilter: string;
  search: string;
  sortKey: BillsSortKey;
  sortDir: BillsSortDir;
  page: number;
  pageSize: number;
  columns: BillColumnState[];
}

export const DEFAULT_BILL_COLUMNS_IMPORT: BillColumnState[] = [
  { id: 'no', visible: true },
  { id: 'company', visible: true },
  { id: 'shipper', visible: true },
  { id: 'consignee', visible: true },
  { id: 'route', visible: true },
  { id: 'vessel', visible: true },
  { id: 'containers', visible: true },
  { id: 'onBoard', visible: true },
  { id: 'eta', visible: true },
  { id: 'type', visible: true },
  { id: 'oceanFreight', visible: true },
  { id: 'note', visible: true },
  { id: 'status', visible: true },
];

export const DEFAULT_BILL_COLUMNS_EXPORT: BillColumnState[] = [
  { id: 'no', visible: true },
  { id: 'company', visible: true },
  { id: 'route', visible: true },
  { id: 'vessel', visible: true },
  { id: 'motherVessel', visible: true },
  { id: 'ts1', visible: true },
  { id: 'ts2', visible: true },
  { id: 'containers', visible: true },
  { id: 'onBoard', visible: true },
  { id: 'eta', visible: true },
  { id: 'note', visible: true },
  { id: 'status', visible: true },
];

/** @deprecated folosește defaultBillColumns(direction) */
export const DEFAULT_BILL_COLUMNS = DEFAULT_BILL_COLUMNS_IMPORT;

export function defaultBillColumns(direction: 'import' | 'export'): BillColumnState[] {
  const src = direction === 'export' ? DEFAULT_BILL_COLUMNS_EXPORT : DEFAULT_BILL_COLUMNS_IMPORT;
  return src.map((c) => ({ ...c }));
}

export function defaultBillsPrefs(direction: 'import' | 'export'): BillsTablePrefs {
  return {
    statusFilter: 'all',
    search: '',
    sortKey: 'onBoard',
    sortDir: 'desc',
    page: 1,
    pageSize: 10,
    columns: defaultBillColumns(direction),
  };
}

export const DEFAULT_BILLS_PREFS: BillsTablePrefs = defaultBillsPrefs('import');

const STORAGE_PREFIX = 'tls-client.bills.v2';

export function billsPrefsKey(direction: 'import' | 'export'): string {
  return `${STORAGE_PREFIX}.${direction}`;
}

const SORT_KEYS: BillsSortKey[] = [
  'no',
  'company',
  'shipper',
  'consignee',
  'route',
  'vessel',
  'motherVessel',
  'ts1',
  'ts2',
  'containers',
  'issued',
  'onBoard',
  'eta',
  'type',
  'oceanFreight',
  'status',
  'note',
];

const PAGE_SIZES = [10, 20, 50, 100];

function normalizeColumns(
  raw: unknown,
  direction: 'import' | 'export',
): BillColumnState[] {
  const defaults = defaultBillColumns(direction);
  const allowed = new Set(defaults.map((c) => c.id));
  const byId = new Map(defaults.map((c) => [c.id, c]));
  if (!Array.isArray(raw)) return defaults;
  const seen = new Set<BillColumnId>();
  const result: BillColumnState[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const id = (item as { id?: string }).id as BillColumnId;
    if (!allowed.has(id) || seen.has(id)) continue;
    seen.add(id);
    const visible = (item as { visible?: boolean }).visible;
    result.push({
      id,
      visible: typeof visible === 'boolean' ? visible : true,
    });
  }
  for (const def of defaults) {
    if (!seen.has(def.id)) result.push({ ...def });
  }
  if (!result.some((c) => c.visible)) {
    result[0] = { ...result[0], visible: true };
  }
  return result;
}

export function loadBillsPrefs(direction: 'import' | 'export'): BillsTablePrefs {
  const fallback = defaultBillsPrefs(direction);
  try {
    const raw = localStorage.getItem(billsPrefsKey(direction));
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<BillsTablePrefs>;
    const rawSort = parsed.sortKey as BillsSortKey;
    const sortKey = rawSort === 'issued'
      ? fallback.sortKey
      : SORT_KEYS.includes(rawSort)
        ? rawSort
        : fallback.sortKey;
    const sortDir = parsed.sortDir === 'asc' ? 'asc' : 'desc';
    const pageSize = PAGE_SIZES.includes(Number(parsed.pageSize))
      ? Number(parsed.pageSize)
      : fallback.pageSize;
    const page =
      typeof parsed.page === 'number' && parsed.page >= 1
        ? Math.floor(parsed.page)
        : 1;
    return {
      statusFilter:
        typeof parsed.statusFilter === 'string' && parsed.statusFilter
          ? parsed.statusFilter
          : 'all',
      search: typeof parsed.search === 'string' ? parsed.search : '',
      sortKey,
      sortDir,
      page,
      pageSize,
      columns: normalizeColumns(parsed.columns, direction),
    };
  } catch {
    return fallback;
  }
}

export function saveBillsPrefs(
  direction: 'import' | 'export',
  prefs: BillsTablePrefs,
): void {
  try {
    localStorage.setItem(billsPrefsKey(direction), JSON.stringify(prefs));
  } catch {
    // Quota / private mode
  }
}

export function billColumnLabel(
  id: BillColumnId,
  lang: 'ro' | 'en',
  etaLabel: string,
  direction: 'import' | 'export' = 'import',
): string {
  const isRo = lang === 'ro';
  const isExport = direction === 'export';
  switch (id) {
    case 'no':
      return 'B/L';
    case 'company':
      return isRo ? 'Firmă' : 'Company';
    case 'shipper':
      return 'Shipper';
    case 'consignee':
      return 'Consignee';
    case 'route':
      return isRo ? 'Rută' : 'Route';
    case 'vessel':
      return isExport
        ? isRo
          ? 'Navă feeder'
          : 'Feeder vessel'
        : isRo
          ? 'Navă'
          : 'Vessel';
    case 'motherVessel':
      return isRo ? 'Navă principală' : 'Mother vessel';
    case 'ts1':
      return isRo ? 'Transshipment 1' : 'Transshipment 1';
    case 'ts2':
      return isRo ? 'Transshipment 2' : 'Transshipment 2';
    case 'containers':
      return isRo ? 'Cont.' : 'Cont.';
    case 'issued':
      return isRo ? 'Emis' : 'Issued';
    case 'onBoard':
      return 'On Board Date';
    case 'eta':
      return etaLabel;
    case 'type':
      return isRo ? 'Tip' : 'Type';
    case 'oceanFreight':
      return 'Ocean Freight';
    case 'status':
      return isRo ? 'Status' : 'Status';
    case 'note':
      return isRo ? 'Notă' : 'Note';
    default:
      return id;
  }
}

export function moveBillColumn(
  columns: BillColumnState[],
  index: number,
  delta: -1 | 1,
): BillColumnState[] {
  const next = [...columns];
  const j = index + delta;
  if (j < 0 || j >= next.length) return columns;
  const tmp = next[index];
  next[index] = next[j];
  next[j] = tmp;
  return next;
}

export function toggleBillColumnVisible(
  columns: BillColumnState[],
  id: BillColumnId,
): BillColumnState[] {
  const visibleCount = columns.filter((c) => c.visible).length;
  return columns.map((c) => {
    if (c.id !== id) return c;
    if (c.visible && visibleCount <= 1) return c;
    return { ...c, visible: !c.visible };
  });
}
